@let currentLanguage = this.currentLanguage();
@let languagesConfig = this.languagesConfig();
@let brightnessConfig = this.brightnessConfig();

<app-header
  class="header"
  coreActivitySection="Header"
  pageName="Preferences"
  [backSwipe]="backSwipeConfig"
>
  <app-header-text slot="center" [text]="'preferences.title' | translate"/>
</app-header>

@if (languagesConfig.allowUserChange && languagesConfig.supported.length > 1) {
  <div class="section" coreActivitySection="Languages">
    <app-text class="title">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 420 420"
        stroke="currentColor"
        fill="none"
      >
        <path stroke-width="26" d="M209,15a195,195 0 1,0 2,0z"/>
        <path
          d="m210,15v390m195-195H15M59,90a260,260 0 0,0 302,0 m0,240 a260,260 0 0,0-302,0M195,20a250,250 0 0,0 0,382 m30,0 a250,250 0 0,0 0-382"
          stroke-width="18"
        />
      </svg>
      {{ 'preferences.language.label' | translate }}
    </app-text>

    <div class="buttons">
      @for (language of languagesConfig.supported; track language) {
        <button
          class="button"
          [class.active]="language === currentLanguage"
          (click)="onLanguageSelect(language)"
          [coreUserActivityClickTracking]="'SelectLanguage' + (`languages.${ language }.labelShort` | translate)"
        >
          <app-text class="icon-text">{{ `languages.${language}.labelShort` | translate }}</app-text>
          <div class="content">
            <app-text class="label">{{ `languages.${language}.label` | translate }}</app-text>
            <app-text class="sublabel">{{ `languages.${language}.labelNative` | translate }}</app-text>
          </div>
        </button>
      }
    </div>
  </div>
}

@if (brightnessConfig.allowUserChange) {
  <div class="section" coreActivitySection="Brightness">
    <app-text class="title">
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 16 16" fill="currentColor">
        <path
          d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0zm0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13zm8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5zM3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8zm10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0zm-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zm9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707zM4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708z"
        />
      </svg>
      {{ 'preferences.brightness.label' | translate }}
    </app-text>

    <div class="buttons">
      @let currentMode = this.currentTintingMode();

      @for (mode of tintingModes; track mode) {
        <button
          class="button"
          [class.active]="currentMode === mode"
          [coreUserActivityClickTracking]="'SelectBrightness' + mode"
          (click)="onTintingChangeMode(mode)"
        >
          @switch (mode) {
            @case ('low') {
              <svg
                class="icon"
                xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm.5-9.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zm0 11a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zm5-5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm-11 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm9.743-4.036a.5.5 0 1 1-.707-.707.5.5 0 0 1 .707.707zm-7.779 7.779a.5.5 0 1 1-.707-.707.5.5 0 0 1 .707.707zm7.072 0a.5.5 0 1 1 .707-.707.5.5 0 0 1-.707.707zM3.757 4.464a.5.5 0 1 1 .707-.707.5.5 0 0 1-.707.707z"
                />
              </svg>
            }
            @case ('medium') {
              <svg
                class="icon"
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 189 189"
                fill="currentColor"
              >
                <path
                  d="M94.5 129.938a35.437 35.437 0 1 1 .001-70.875 35.437 35.437 0 0 1-.001 70.875Zm0 11.812a47.251 47.251 0 0 0 33.411-80.66A47.251 47.251 0 1 0 94.5 141.75ZM98.676 15.23a5.907 5.907 0 0 0-10.082 4.176v10.125a5.906 5.906 0 1 0 11.812 0V19.406a5.906 5.906 0 0 0-1.73-4.176ZM98.676 155.73a5.905 5.905 0 0 0-10.082 4.176v10.125a5.907 5.907 0 1 0 11.812 0v-10.125a5.907 5.907 0 0 0-1.73-4.176ZM153.418 41.353a5.905 5.905 0 0 0-10.082-4.177l-7.16 7.16a5.909 5.909 0 0 0 0 8.352 5.908 5.908 0 0 0 8.353 0l7.159-7.159a5.905 5.905 0 0 0 1.73-4.176ZM54.418 140.353a5.908 5.908 0 0 0-10.082-4.177l-7.16 7.16a5.905 5.905 0 1 0 8.353 8.353l7.16-7.16a5.907 5.907 0 0 0 1.73-4.176ZM134.446 140.353a5.91 5.91 0 0 1 5.907-5.907 5.91 5.91 0 0 1 4.176 1.73l7.159 7.16a5.904 5.904 0 0 1 0 8.353 5.905 5.905 0 0 1-8.352 0l-7.16-7.16a5.91 5.91 0 0 1-1.73-4.176ZM35.447 41.353a5.906 5.906 0 0 1 10.082-4.177l7.16 7.16a5.906 5.906 0 1 1-8.353 8.352l-7.16-7.159a5.906 5.906 0 0 1-1.73-4.176ZM173.208 99.083A5.908 5.908 0 0 0 169.031 89h-10.125a5.907 5.907 0 0 0 0 11.812h10.125a5.908 5.908 0 0 0 4.177-1.73ZM34.208 99.083A5.906 5.906 0 0 0 30.03 89H19.906a5.906 5.906 0 0 0 0 11.812h10.125a5.907 5.907 0 0 0 4.177-1.73Z"
                />
              </svg>
            }
            @case ('high') {
              <svg
                class="icon"
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0zm0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13zm8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5zM3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8zm10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0zm-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zm9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707zM4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708z"
                />
              </svg>
            }
          }
          <div class="content">
            <app-text class="label">{{ `preferences.brightness.modes.${mode}.label` | translate }}</app-text>
            <app-text class="description">{{ `preferences.brightness.modes.${mode}.description` | translate }}
            </app-text>
          </div>
        </button>
      }
    </div>
  </div>
}
